import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Sparkles as SparklesIcon,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Play,
  Pause,
  Heart,
  Moon,
  Sun,
  Shield,
  Star
} from 'lucide-react';
import { getDhikrToday, incrementDhikr as incrementDhikrAPI, resetDhikr as resetDhikrAPI } from '../lib/api';

// 99 Names of Allah (Esma ul Husna) - Complete list with audio URLs
// Audio source: Islamic audio resources
const ESMA_AUDIO_BASE_URL = 'https://www.islamcan.com/audio/99-names-of-allah';

const ESMA_UL_HUSNA = [
  { number: 1, arabic: "الرَّحْمَنُ", transliteration: "Ar-Rahman", meaning_en: "The Most Gracious", meaning_hr: "Svemilosni" },
  { number: 2, arabic: "الرَّحِيمُ", transliteration: "Ar-Rahim", meaning_en: "The Most Merciful", meaning_hr: "Milostivi" },
  { number: 3, arabic: "الْمَلِكُ", transliteration: "Al-Malik", meaning_en: "The King", meaning_hr: "Vladar" },
  { number: 4, arabic: "الْقُدُّوسُ", transliteration: "Al-Quddus", meaning_en: "The Most Holy", meaning_hr: "Sveti" },
  { number: 5, arabic: "السَّلاَمُ", transliteration: "As-Salam", meaning_en: "The Source of Peace", meaning_hr: "Mir" },
  { number: 6, arabic: "الْمُؤْمِنُ", transliteration: "Al-Mu'min", meaning_en: "The Guardian of Faith", meaning_hr: "Zaštitnik vjere" },
  { number: 7, arabic: "الْمُهَيْمِنُ", transliteration: "Al-Muhaymin", meaning_en: "The Protector", meaning_hr: "Zaštitnik" },
  { number: 8, arabic: "الْعَزِيزُ", transliteration: "Al-Aziz", meaning_en: "The Almighty", meaning_hr: "Svemoćni" },
  { number: 9, arabic: "الْجَبَّارُ", transliteration: "Al-Jabbar", meaning_en: "The Compeller", meaning_hr: "Onaj koji prisiljava" },
  { number: 10, arabic: "الْمُتَكَبِّرُ", transliteration: "Al-Mutakabbir", meaning_en: "The Greatest", meaning_hr: "Veličanstveni" },
  { number: 11, arabic: "الْخَالِقُ", transliteration: "Al-Khaliq", meaning_en: "The Creator", meaning_hr: "Stvoritelj" },
  { number: 12, arabic: "الْبَارِئُ", transliteration: "Al-Bari'", meaning_en: "The Maker", meaning_hr: "Tvorac" },
  { number: 13, arabic: "الْمُصَوِّرُ", transliteration: "Al-Musawwir", meaning_en: "The Fashioner", meaning_hr: "Oblikovatelj" },
  { number: 14, arabic: "الْغَفَّارُ", transliteration: "Al-Ghaffar", meaning_en: "The Forgiver", meaning_hr: "Onaj koji oprašta" },
  { number: 15, arabic: "الْقَهَّارُ", transliteration: "Al-Qahhar", meaning_en: "The Subduer", meaning_hr: "Pobjednik" },
  { number: 16, arabic: "الْوَهَّابُ", transliteration: "Al-Wahhab", meaning_en: "The Bestower", meaning_hr: "Darovatelj" },
  { number: 17, arabic: "الرَّزَّاقُ", transliteration: "Ar-Razzaq", meaning_en: "The Provider", meaning_hr: "Opskrbitelj" },
  { number: 18, arabic: "الْفَتَّاحُ", transliteration: "Al-Fattah", meaning_en: "The Opener", meaning_hr: "Otvarač" },
  { number: 19, arabic: "اَلْعَلِيْمُ", transliteration: "Al-'Alim", meaning_en: "The All-Knowing", meaning_hr: "Sveznajući" },
  { number: 20, arabic: "الْقَابِضُ", transliteration: "Al-Qabid", meaning_en: "The Constrictor", meaning_hr: "Onaj koji uskraćuje" },
  { number: 21, arabic: "الْبَاسِطُ", transliteration: "Al-Basit", meaning_en: "The Expander", meaning_hr: "Onaj koji proširuje" },
  { number: 22, arabic: "الْخَافِضُ", transliteration: "Al-Khafid", meaning_en: "The Abaser", meaning_hr: "Onaj koji ponižava" },
  { number: 23, arabic: "الرَّافِعُ", transliteration: "Ar-Rafi'", meaning_en: "The Exalter", meaning_hr: "Onaj koji uzvisuje" },
  { number: 24, arabic: "الْمُعِزُّ", transliteration: "Al-Mu'izz", meaning_en: "The Honorer", meaning_hr: "Onaj koji uzdiže" },
  { number: 25, arabic: "المُذِلُّ", transliteration: "Al-Mudhill", meaning_en: "The Dishonorer", meaning_hr: "Onaj koji ponižava" },
  { number: 26, arabic: "السَّمِيعُ", transliteration: "As-Sami'", meaning_en: "The All-Hearing", meaning_hr: "Svečujući" },
  { number: 27, arabic: "الْبَصِيرُ", transliteration: "Al-Basir", meaning_en: "The All-Seeing", meaning_hr: "Svevideći" },
  { number: 28, arabic: "الْحَكَمُ", transliteration: "Al-Hakam", meaning_en: "The Judge", meaning_hr: "Sudac" },
  { number: 29, arabic: "الْعَدْلُ", transliteration: "Al-'Adl", meaning_en: "The Just", meaning_hr: "Pravedni" },
  { number: 30, arabic: "اللَّطِيفُ", transliteration: "Al-Latif", meaning_en: "The Subtle One", meaning_hr: "Blag" },
  { number: 31, arabic: "الْخَبِيرُ", transliteration: "Al-Khabir", meaning_en: "The All-Aware", meaning_hr: "Obaviješteni" },
  { number: 32, arabic: "الْحَلِيمُ", transliteration: "Al-Halim", meaning_en: "The Forbearing", meaning_hr: "Blag" },
  { number: 33, arabic: "الْعَظِيمُ", transliteration: "Al-'Azim", meaning_en: "The Magnificent", meaning_hr: "Veličanstveni" },
  { number: 34, arabic: "الْغَفُورُ", transliteration: "Al-Ghafur", meaning_en: "The Forgiving", meaning_hr: "Onaj koji prašta" },
  { number: 35, arabic: "الشَّكُورُ", transliteration: "Ash-Shakur", meaning_en: "The Appreciative", meaning_hr: "Zahvalni" },
  { number: 36, arabic: "الْعَلِيُّ", transliteration: "Al-'Ali", meaning_en: "The Most High", meaning_hr: "Uzvišeni" },
  { number: 37, arabic: "الْكَبِيرُ", transliteration: "Al-Kabir", meaning_en: "The Most Great", meaning_hr: "Veliki" },
  { number: 38, arabic: "الْحَفِيظُ", transliteration: "Al-Hafiz", meaning_en: "The Preserver", meaning_hr: "Čuvar" },
  { number: 39, arabic: "المُقيِت", transliteration: "Al-Muqit", meaning_en: "The Nourisher", meaning_hr: "Hranitelj" },
  { number: 40, arabic: "اَلْحَسِيبُ", transliteration: "Al-Hasib", meaning_en: "The Reckoner", meaning_hr: "Obračunavatelj" },
  { number: 41, arabic: "الْجَلِيلُ", transliteration: "Al-Jalil", meaning_en: "The Majestic", meaning_hr: "Veličanstveni" },
  { number: 42, arabic: "الْكَرِيمُ", transliteration: "Al-Karim", meaning_en: "The Generous", meaning_hr: "Plemeniti" },
  { number: 43, arabic: "الرَّقِيبُ", transliteration: "Ar-Raqib", meaning_en: "The Watchful", meaning_hr: "Nadziratelj" },
  { number: 44, arabic: "الْمُجِيبُ", transliteration: "Al-Mujib", meaning_en: "The Responsive", meaning_hr: "Onaj koji odgovara" },
  { number: 45, arabic: "الْوَاسِعُ", transliteration: "Al-Wasi'", meaning_en: "The All-Encompassing", meaning_hr: "Sveobuhvatni" },
  { number: 46, arabic: "الْحَكِيمُ", transliteration: "Al-Hakim", meaning_en: "The Wise", meaning_hr: "Mudri" },
  { number: 47, arabic: "الْوَدُودُ", transliteration: "Al-Wadud", meaning_en: "The Loving", meaning_hr: "Onaj koji voli" },
  { number: 48, arabic: "الْمَجِيدُ", transliteration: "Al-Majid", meaning_en: "The Glorious", meaning_hr: "Slavni" },
  { number: 49, arabic: "الْبَاعِثُ", transliteration: "Al-Ba'ith", meaning_en: "The Resurrector", meaning_hr: "Onaj koji proživljava" },
  { number: 50, arabic: "الشَّهِيدُ", transliteration: "Ash-Shahid", meaning_en: "The Witness", meaning_hr: "Svjedok" },
  { number: 51, arabic: "الْحَقُّ", transliteration: "Al-Haqq", meaning_en: "The Truth", meaning_hr: "Istina" },
  { number: 52, arabic: "الْوَكِيلُ", transliteration: "Al-Wakil", meaning_en: "The Trustee", meaning_hr: "Zaštitnik" },
  { number: 53, arabic: "الْقَوِيُّ", transliteration: "Al-Qawiyy", meaning_en: "The Most Strong", meaning_hr: "Snažni" },
  { number: 54, arabic: "الْمَتِينُ", transliteration: "Al-Matin", meaning_en: "The Firm One", meaning_hr: "Čvrsti" },
  { number: 55, arabic: "الْوَلِيُّ", transliteration: "Al-Waliyy", meaning_en: "The Protecting Friend", meaning_hr: "Zaštitnik" },
  { number: 56, arabic: "الْحَمِيدُ", transliteration: "Al-Hamid", meaning_en: "The Praiseworthy", meaning_hr: "Hvaljeni" },
  { number: 57, arabic: "الْمُحْصِي", transliteration: "Al-Muhsi", meaning_en: "The Appraiser", meaning_hr: "Brojač" },
  { number: 58, arabic: "الْمُبْدِئُ", transliteration: "Al-Mubdi'", meaning_en: "The Originator", meaning_hr: "Začetnik" },
  { number: 59, arabic: "الْمُعِيدُ", transliteration: "Al-Mu'id", meaning_en: "The Restorer", meaning_hr: "Onaj koji vraća" },
  { number: 60, arabic: "الْمُحْيِي", transliteration: "Al-Muhyi", meaning_en: "The Giver of Life", meaning_hr: "Oživljitelj" },
  { number: 61, arabic: "اَلْمُمِيتُ", transliteration: "Al-Mumit", meaning_en: "The Taker of Life", meaning_hr: "Onaj koji usmrćuje" },
  { number: 62, arabic: "الْحَيُّ", transliteration: "Al-Hayy", meaning_en: "The Ever Living", meaning_hr: "Živi" },
  { number: 63, arabic: "الْقَيُّومُ", transliteration: "Al-Qayyum", meaning_en: "The Self-Existing", meaning_hr: "Samoopstojeći" },
  { number: 64, arabic: "الْوَاجِدُ", transliteration: "Al-Wajid", meaning_en: "The Finder", meaning_hr: "Pronalazač" },
  { number: 65, arabic: "الْمَاجِدُ", transliteration: "Al-Majid", meaning_en: "The Noble", meaning_hr: "Plemeniti" },
  { number: 66, arabic: "الْواحِدُ", transliteration: "Al-Wahid", meaning_en: "The One", meaning_hr: "Jedini" },
  { number: 67, arabic: "اَلاَحَدُ", transliteration: "Al-Ahad", meaning_en: "The Unique", meaning_hr: "Jedinstveni" },
  { number: 68, arabic: "الصَّمَدُ", transliteration: "As-Samad", meaning_en: "The Eternal", meaning_hr: "Utočište" },
  { number: 69, arabic: "الْقَادِرُ", transliteration: "Al-Qadir", meaning_en: "The Able", meaning_hr: "Moćni" },
  { number: 70, arabic: "الْمُقْتَدِرُ", transliteration: "Al-Muqtadir", meaning_en: "The Powerful", meaning_hr: "Svemoćni" },
  { number: 71, arabic: "الْمُقَدِّمُ", transliteration: "Al-Muqaddim", meaning_en: "The Expediter", meaning_hr: "Onaj koji unapređuje" },
  { number: 72, arabic: "الْمُؤَخِّرُ", transliteration: "Al-Mu'akhkhir", meaning_en: "The Delayer", meaning_hr: "Onaj koji odgađa" },
  { number: 73, arabic: "الأوَّلُ", transliteration: "Al-Awwal", meaning_en: "The First", meaning_hr: "Prvi" },
  { number: 74, arabic: "الآخِرُ", transliteration: "Al-Akhir", meaning_en: "The Last", meaning_hr: "Posljednji" },
  { number: 75, arabic: "الظَّاهِرُ", transliteration: "Az-Zahir", meaning_en: "The Manifest", meaning_hr: "Očiti" },
  { number: 76, arabic: "الْبَاطِنُ", transliteration: "Al-Batin", meaning_en: "The Hidden", meaning_hr: "Skriveni" },
  { number: 77, arabic: "الْوَالِي", transliteration: "Al-Wali", meaning_en: "The Governor", meaning_hr: "Upravitelj" },
  { number: 78, arabic: "الْمُتَعَالِي", transliteration: "Al-Muta'ali", meaning_en: "The Most Exalted", meaning_hr: "Uzvišeni" },
  { number: 79, arabic: "الْبَرُّ", transliteration: "Al-Barr", meaning_en: "The Source of Goodness", meaning_hr: "Dobročinitelj" },
  { number: 80, arabic: "التَّوَابُ", transliteration: "At-Tawwab", meaning_en: "The Acceptor of Repentance", meaning_hr: "Primatelj pokajanja" },
  { number: 81, arabic: "الْمُنْتَقِمُ", transliteration: "Al-Muntaqim", meaning_en: "The Avenger", meaning_hr: "Osvetnik" },
  { number: 82, arabic: "العَفُوُّ", transliteration: "Al-'Afuw", meaning_en: "The Pardoner", meaning_hr: "Onaj koji briše grijehe" },
  { number: 83, arabic: "الرَّؤُوفُ", transliteration: "Ar-Ra'uf", meaning_en: "The Compassionate", meaning_hr: "Samilosni" },
  { number: 84, arabic: "مَالِكُ الْمُلْكِ", transliteration: "Malik-ul-Mulk", meaning_en: "Owner of Sovereignty", meaning_hr: "Vlasnik svega" },
  { number: 85, arabic: "ذُوالْجَلاَلِ وَالإكْرَامِ", transliteration: "Dhul-Jalali wal-Ikram", meaning_en: "Lord of Majesty and Bounty", meaning_hr: "Gospodar veličanstva" },
  { number: 86, arabic: "الْمُقْسِطُ", transliteration: "Al-Muqsit", meaning_en: "The Equitable", meaning_hr: "Pravični" },
  { number: 87, arabic: "الْجَامِعُ", transliteration: "Al-Jami'", meaning_en: "The Gatherer", meaning_hr: "Sakupljač" },
  { number: 88, arabic: "الْغَنِيُّ", transliteration: "Al-Ghani", meaning_en: "The Self-Sufficient", meaning_hr: "Bogati" },
  { number: 89, arabic: "الْمُغْنِي", transliteration: "Al-Mughni", meaning_en: "The Enricher", meaning_hr: "Onaj koji obogaćuje" },
  { number: 90, arabic: "اَلْمَانِعُ", transliteration: "Al-Mani'", meaning_en: "The Preventer", meaning_hr: "Onaj koji sprječava" },
  { number: 91, arabic: "الضَّارَّ", transliteration: "Ad-Darr", meaning_en: "The Distresser", meaning_hr: "Onaj koji šteti" },
  { number: 92, arabic: "النَّافِعُ", transliteration: "An-Nafi'", meaning_en: "The Propitious", meaning_hr: "Onaj koji koristi" },
  { number: 93, arabic: "النُّورُ", transliteration: "An-Nur", meaning_en: "The Light", meaning_hr: "Svjetlo" },
  { number: 94, arabic: "الْهَادِي", transliteration: "Al-Hadi", meaning_en: "The Guide", meaning_hr: "Upućivač" },
  { number: 95, arabic: "الْبَدِيعُ", transliteration: "Al-Badi'", meaning_en: "The Incomparable", meaning_hr: "Tvorac bez uzora" },
  { number: 96, arabic: "اَلْبَاقِي", transliteration: "Al-Baqi", meaning_en: "The Everlasting", meaning_hr: "Vječni" },
  { number: 97, arabic: "الْوَارِثُ", transliteration: "Al-Warith", meaning_en: "The Heir", meaning_hr: "Nasljednik" },
  { number: 98, arabic: "الرَّشِيدُ", transliteration: "Ar-Rashid", meaning_en: "The Guide to the Right Path", meaning_hr: "Upućivač na pravi put" },
  { number: 99, arabic: "الصَّبُورُ", transliteration: "As-Sabur", meaning_en: "The Patient", meaning_hr: "Strpljivi" },
];

// Hisnul Muslim Duas - Selected important duas
const HISNUL_MUSLIM_DUAS = [
  {
    id: 1,
    category_en: "Morning",
    category_hr: "Jutarnje",
    title_en: "Morning Remembrance",
    title_hr: "Jutarnji zikr",
    arabic: "أَصْبَحْنَا وَأَصْبَحَ الْمُلْكُ لِلَّهِ، وَالْحَمْدُ لِلَّهِ، لَا إِلَٰهَ إِلَّا اللَّهُ وَحْدَهُ لَا شَرِيكَ لَهُ",
    transliteration: "Asbahna wa asbahal-mulku lillah, walhamdu lillah, la ilaha illallahu wahdahu la sharika lah",
    translation_en: "We have entered a new day and with it all dominion belongs to Allah. Praise is to Allah. None has the right to be worshipped but Allah alone, with no partner.",
    translation_hr: "Osvanuli smo i osvanula je vlast Allahova, hvala Allahu, nema boga osim Allaha, Jedinog, koji nema sudruga.",
    icon: Sun
  },
  {
    id: 2,
    category_en: "Evening",
    category_hr: "Večernje",
    title_en: "Evening Remembrance",
    title_hr: "Večernji zikr",
    arabic: "أَمْسَيْنَا وَأَمْسَى الْمُلْكُ لِلَّهِ، وَالْحَمْدُ لِلَّهِ، لَا إِلَٰهَ إِلَّا اللَّهُ وَحْدَهُ لَا شَرِيكَ لَهُ",
    transliteration: "Amsayna wa amsal-mulku lillah, walhamdu lillah, la ilaha illallahu wahdahu la sharika lah",
    translation_en: "We have entered a new evening and with it all dominion belongs to Allah. Praise is to Allah. None has the right to be worshipped but Allah alone, with no partner.",
    translation_hr: "Omrknuli smo i omrknula je vlast Allahova, hvala Allahu, nema boga osim Allaha, Jedinog, koji nema sudruga.",
    icon: Moon
  },
  {
    id: 3,
    category_en: "Protection",
    category_hr: "Zaštita",
    title_en: "Seeking Refuge",
    title_hr: "Traženje utočišta",
    arabic: "بِسْمِ اللَّهِ الَّذِي لَا يَضُرُّ مَعَ اسْمِهِ شَيْءٌ فِي الْأَرْضِ وَلَا فِي السَّمَاءِ وَهُوَ السَّمِيعُ الْعَلِيمُ",
    transliteration: "Bismillahil-ladhi la yadurru ma'asmihi shay'un fil-ardi wa la fis-sama'i wa huwas-Sami'ul-'Alim",
    translation_en: "In the Name of Allah, with Whose Name nothing on earth or in heaven can cause harm, and He is the All-Hearing, the All-Knowing.",
    translation_hr: "U ime Allaha s čijim imenom ništa ne može naškoditi ni na Zemlji ni na nebu, a On sve čuje i sve zna.",
    icon: Shield
  },
  {
    id: 4,
    category_en: "Before Sleep",
    category_hr: "Prije spavanja",
    title_en: "Before Sleeping",
    title_hr: "Prije spavanja",
    arabic: "بِاسْمِكَ اللَّهُمَّ أَمُوتُ وَأَحْيَا",
    transliteration: "Bismika Allahumma amutu wa ahya",
    translation_en: "In Your name, O Allah, I die and I live.",
    translation_hr: "U Tvoje ime, Allahu, umirem i živim.",
    icon: Moon
  },
  {
    id: 5,
    category_en: "Waking Up",
    category_hr: "Buđenje",
    title_en: "Upon Waking",
    title_hr: "Prilikom buđenja",
    arabic: "الْحَمْدُ لِلَّهِ الَّذِي أَحْيَانَا بَعْدَ مَا أَمَاتَنَا وَإِلَيْهِ النُّشُورُ",
    transliteration: "Alhamdu lillahil-ladhi ahyana ba'da ma amatana wa ilayhin-nushur",
    translation_en: "Praise be to Allah Who gave us life after death and to Him is the resurrection.",
    translation_hr: "Hvala Allahu koji nas je oživio nakon što nas je usmrtio i Njemu se vraćamo.",
    icon: Sun
  },
  {
    id: 6,
    category_en: "Anxiety",
    category_hr: "Tjeskoba",
    title_en: "When Distressed",
    title_hr: "U nevolji",
    arabic: "لَا إِلَٰهَ إِلَّا اللَّهُ الْعَظِيمُ الْحَلِيمُ، لَا إِلَٰهَ إِلَّا اللَّهُ رَبُّ الْعَرْشِ الْعَظِيمِ",
    transliteration: "La ilaha illallahul-'Azimul-Halim, la ilaha illallahu Rabbul-'Arshil-'Azim",
    translation_en: "There is none worthy of worship but Allah, the Mighty, the Forbearing. There is none worthy of worship but Allah, Lord of the Magnificent Throne.",
    translation_hr: "Nema boga osim Allaha, Velikog, Blagog. Nema boga osim Allaha, Gospodara Arša veličanstvenog.",
    icon: Heart
  },
  {
    id: 7,
    category_en: "Forgiveness",
    category_hr: "Oprost",
    title_en: "Seeking Forgiveness",
    title_hr: "Traženje oprosta",
    arabic: "أَسْتَغْفِرُ اللَّهَ الْعَظِيمَ الَّذِي لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ وَأَتُوبُ إِلَيْهِ",
    transliteration: "Astaghfirullaha al-'Azim alladhi la ilaha illa Huwal-Hayyul-Qayyum wa atubu ilayh",
    translation_en: "I seek forgiveness from Allah, the Magnificent, whom there is none worthy of worship but He, the Living, the Self-Subsisting, and I turn to Him in repentance.",
    translation_hr: "Tražim oprosta od Allaha Velikog, osim Kojeg drugog boga nema, Živog, Samoopstojeće, i Njemu se kajem.",
    icon: Star
  },
  {
    id: 8,
    category_en: "Travel",
    category_hr: "Putovanje",
    title_en: "When Traveling",
    title_hr: "Prilikom putovanja",
    arabic: "سُبْحَانَ الَّذِي سَخَّرَ لَنَا هَٰذَا وَمَا كُنَّا لَهُ مُقْرِنِينَ وَإِنَّا إِلَىٰ رَبِّنَا لَمُنْقَلِبُونَ",
    transliteration: "Subhanal-ladhi sakhkhara lana hadha wa ma kunna lahu muqrinin, wa inna ila Rabbina lamunqalibun",
    translation_en: "Glory be to Him Who has subjected this to us, for we could never have accomplished this by ourselves. Surely, unto our Lord we are returning.",
    translation_hr: "Slavljen neka je Onaj koji nam je ovo potčinio, jer mi to sami ne bismo mogli postići i mi se Gospodaru svome vraćamo.",
    icon: BookOpen
  },
];

// Tabs for the page
const TABS = {
  counter: { label_en: 'Counter', label_hr: 'Brojač' },
  esma: { label_en: 'Esma ul Husna', label_hr: 'Esma ul Husna' },
  hisnul: { label_en: 'Hisnul Muslim', label_hr: 'Hisnul Muslim' },
};

export default function Dhikr({ user }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  
  const [activeTab, setActiveTab] = useState('counter');
  const [dhikrCount, setDhikrCount] = useState(null);
  const [incrementing, setIncrementing] = useState(false);
  
  // Esma ul Husna state
  const [currentNameIndex, setCurrentNameIndex] = useState(0);
  const [showAllNames, setShowAllNames] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  
  // Hisnul Muslim state
  const [selectedDuaCategory, setSelectedDuaCategory] = useState('all');

  useEffect(() => {
    fetchDhikr();
  }, []);

  const fetchDhikr = async () => {
    try {
      const data = await getDhikrToday();
      setDhikrCount(data);
    } catch (error) {
      console.error('Error fetching dhikr:', error);
      toast.error(lang === 'hr' ? 'Greška pri učitavanju' : 'Error loading dhikr');
    }
  };

  const incrementDhikr = async (amount = 1) => {
    setIncrementing(true);
    try {
      const data = await incrementDhikrAPI(amount);
      setDhikrCount(data);
    } catch (error) {
      console.error('Error incrementing dhikr:', error);
      toast.error(lang === 'hr' ? 'Greška' : 'Error');
    } finally {
      setIncrementing(false);
    }
  };

  const resetDhikr = async () => {
    try {
      const data = await resetDhikrAPI();
      setDhikrCount(data);
      toast.success(lang === 'hr' ? 'Brojač resetovan' : 'Counter reset');
    } catch (error) {
      console.error('Error resetting dhikr:', error);
      toast.error(lang === 'hr' ? 'Greška' : 'Error');
    }
  };

  const currentName = ESMA_UL_HUSNA[currentNameIndex];
  
  const nextName = () => {
    setCurrentNameIndex((prev) => (prev + 1) % 99);
  };
  
  const prevName = () => {
    setCurrentNameIndex((prev) => (prev - 1 + 99) % 99);
  };

  // Audio playback for Esma ul Husna using Web Speech API
  const playNameAudio = (name) => {
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(name.arabic);
        utterance.lang = 'ar-SA';
        utterance.rate = 0.6;
        utterance.pitch = 1;
        
        utterance.onstart = () => setIsPlaying(true);
        utterance.onend = () => {
          setIsPlaying(false);
          // Auto-play next if enabled
          if (isAutoPlay && currentNameIndex < ESMA_UL_HUSNA.length - 1) {
            setTimeout(() => {
              setCurrentNameIndex(prev => prev + 1);
            }, 1500);
          }
        };
        utterance.onerror = () => setIsPlaying(false);
        
        window.speechSynthesis.speak(utterance);
      } else {
        toast.error(lang === 'hr' ? 'Audio nije podržan u ovom pregledniku' : 'Audio not supported in this browser');
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsAutoPlay(false);
  };

  const toggleAutoPlay = () => {
    if (isAutoPlay) {
      stopAudio();
    } else {
      setIsAutoPlay(true);
      playNameAudio(currentName);
    }
  };

  // Auto-play effect when name changes and autoPlay is on
  useEffect(() => {
    if (isAutoPlay && activeTab === 'esma') {
      playNameAudio(ESMA_UL_HUSNA[currentNameIndex]);
    }
  }, [currentNameIndex, isAutoPlay, activeTab]);

  const filteredDuas = selectedDuaCategory === 'all' 
    ? HISNUL_MUSLIM_DUAS 
    : HISNUL_MUSLIM_DUAS.filter(d => d.category_en.toLowerCase() === selectedDuaCategory.toLowerCase());

  const duaCategories = ['all', ...new Set(HISNUL_MUSLIM_DUAS.map(d => d.category_en.toLowerCase()))];

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <SparklesIcon className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
            {lang === 'hr' ? 'Zikr i Dove' : 'Dhikr & Duas'}
          </h1>
          <p className="text-[#57534E] font-body">
            {lang === 'hr' ? 'Spominjanje Allaha i dove' : 'Remember Allah and supplicate'}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-stone-100 p-1 rounded-xl">
            {Object.entries(TABS).map(([key, labels]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === key
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-[#57534E] hover:text-[#292524]'
                }`}
              >
                {lang === 'hr' ? labels.label_hr : labels.label_en}
              </button>
            ))}
          </div>
        </div>

        {/* Counter Tab */}
        {activeTab === 'counter' && (
          <div className="space-y-8">
            {/* Main Counter */}
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-12 shadow-card text-center">
              <div className="text-8xl font-heading font-bold text-primary mb-6">
                {dhikrCount?.count || 0}
              </div>
              <p className="text-lg text-[#57534E] font-body mb-8">
                {lang === 'hr' ? 'Današnji brojač' : "Today's Count"}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => incrementDhikr(1)}
                  disabled={incrementing}
                  className="w-full sm:w-auto px-12 py-6 bg-primary text-white rounded-full font-heading font-semibold text-xl hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 shadow-lg"
                >
                  +1
                </button>
                <button
                  onClick={() => incrementDhikr(33)}
                  disabled={incrementing}
                  className="w-full sm:w-auto px-12 py-6 bg-accent text-white rounded-full font-heading font-semibold text-xl hover:bg-accent/90 transition-all active:scale-95 disabled:opacity-50 shadow-lg"
                >
                  +33
                </button>
                <button
                  onClick={resetDhikr}
                  disabled={incrementing}
                  className="w-full sm:w-auto px-8 py-6 bg-stone-200 text-stone-700 rounded-full font-heading font-semibold text-xl hover:bg-stone-300 transition-all active:scale-95 disabled:opacity-50"
                >
                  <RotateCcw className="w-5 h-5 inline-block mr-2" />
                  {lang === 'hr' ? 'Resetuj' : 'Reset'}
                </button>
              </div>
            </div>

            {/* Quick Dhikr Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-6 shadow-card">
                <h3 className="font-heading font-semibold text-lg text-[#292524] mb-3">
                  Subhanallah
                </h3>
                <p className="text-sm text-[#57534E] font-body mb-2">
                  {lang === 'hr' ? 'Slavljen neka je Allah' : 'Glory be to Allah'}
                </p>
                <p className="font-arabic text-2xl text-primary">سُبْحَانَ الله</p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-6 shadow-card">
                <h3 className="font-heading font-semibold text-lg text-[#292524] mb-3">
                  Alhamdulillah
                </h3>
                <p className="text-sm text-[#57534E] font-body mb-2">
                  {lang === 'hr' ? 'Hvala Allahu' : 'All praise is due to Allah'}
                </p>
                <p className="font-arabic text-2xl text-primary">الْحَمْدُ لِله</p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-6 shadow-card">
                <h3 className="font-heading font-semibold text-lg text-[#292524] mb-3">
                  Allahu Akbar
                </h3>
                <p className="text-sm text-[#57534E] font-body mb-2">
                  {lang === 'hr' ? 'Allah je najveći' : 'Allah is the Greatest'}
                </p>
                <p className="font-arabic text-2xl text-primary">اللهُ أَكْبَر</p>
              </div>
            </div>
          </div>
        )}

        {/* Esma ul Husna Tab */}
        {activeTab === 'esma' && (
          <div className="space-y-8">
            {/* Featured Name Card */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-stone-200/60 rounded-2xl p-8 shadow-card">
              <div className="flex items-center justify-between mb-6">
                <button 
                  onClick={prevName}
                  className="p-3 bg-white rounded-full shadow-sm hover:shadow-md transition-all"
                >
                  <ChevronLeft className="w-6 h-6 text-primary" />
                </button>
                
                <div className="text-center flex-1 px-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full text-lg font-bold mb-4">
                    {currentName.number}
                  </div>
                  <p className="font-arabic text-5xl md:text-6xl text-primary mb-4 leading-relaxed">
                    {currentName.arabic}
                  </p>
                  <h3 className="text-2xl font-heading font-bold text-[#292524] mb-2">
                    {currentName.transliteration}
                  </h3>
                  <p className="text-lg text-[#57534E]">
                    {lang === 'hr' ? currentName.meaning_hr : currentName.meaning_en}
                  </p>
                </div>
                
                <button 
                  onClick={nextName}
                  className="p-3 bg-white rounded-full shadow-sm hover:shadow-md transition-all"
                >
                  <ChevronRight className="w-6 h-6 text-primary" />
                </button>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-[#A8A29E]">
                  {currentName.number} / 99
                </p>
              </div>

              {/* Audio Controls */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={() => playNameAudio(currentName)}
                  disabled={isPlaying}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                    isPlaying 
                      ? 'bg-primary text-white' 
                      : 'bg-white border border-stone-200 text-[#292524] hover:bg-stone-50'
                  }`}
                >
                  {isPlaying ? (
                    <>
                      <Pause className="w-5 h-5" />
                      {lang === 'hr' ? 'Reproducira se...' : 'Playing...'}
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      {lang === 'hr' ? 'Slušaj' : 'Listen'}
                    </>
                  )}
                </button>

                <button
                  onClick={toggleAutoPlay}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                    isAutoPlay 
                      ? 'bg-accent text-white' 
                      : 'bg-white border border-stone-200 text-[#292524] hover:bg-stone-50'
                  }`}
                >
                  {isAutoPlay ? (
                    <>
                      <Pause className="w-5 h-5" />
                      {lang === 'hr' ? 'Zaustavi auto' : 'Stop Auto'}
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      {lang === 'hr' ? 'Auto učenje' : 'Auto Learn'}
                    </>
                  )}
                </button>
              </div>

              {isAutoPlay && (
                <p className="text-center text-sm text-accent mt-3">
                  {lang === 'hr' 
                    ? '🔊 Automatsko učenje aktivno - svako ime će se izgovoriti redom' 
                    : '🔊 Auto learning active - each name will be pronounced in order'}
                </p>
              )}
            </div>

            {/* Show All Names Toggle */}
            <div className="text-center">
              <button
                onClick={() => setShowAllNames(!showAllNames)}
                className="px-6 py-3 bg-white border border-stone-200 rounded-xl font-medium text-[#292524] hover:bg-stone-50 transition-all"
              >
                {showAllNames 
                  ? (lang === 'hr' ? 'Sakrij sva imena' : 'Hide all names')
                  : (lang === 'hr' ? 'Prikaži svih 99 imena' : 'Show all 99 names')}
              </button>
            </div>

            {/* All Names Grid */}
            {showAllNames && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ESMA_UL_HUSNA.map((name) => (
                  <div 
                    key={name.number}
                    onClick={() => {
                      setCurrentNameIndex(name.number - 1);
                      setShowAllNames(false);
                    }}
                    className={`bg-white/80 backdrop-blur-sm border rounded-xl p-4 shadow-card cursor-pointer hover:border-primary/50 transition-all ${
                      currentNameIndex === name.number - 1 ? 'border-primary ring-1 ring-primary/30' : 'border-stone-200/60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full text-sm font-bold">
                        {name.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-arabic text-xl text-primary mb-1 truncate">
                          {name.arabic}
                        </p>
                        <p className="text-sm font-medium text-[#292524] truncate">
                          {name.transliteration}
                        </p>
                        <p className="text-xs text-[#57534E] truncate">
                          {lang === 'hr' ? name.meaning_hr : name.meaning_en}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hisnul Muslim Tab */}
        {activeTab === 'hisnul' && (
          <div className="space-y-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 justify-center">
              {duaCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedDuaCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedDuaCategory === cat
                      ? 'bg-primary text-white'
                      : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                  }`}
                >
                  {cat === 'all' 
                    ? (lang === 'hr' ? 'Sve' : 'All')
                    : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>

            {/* Duas List */}
            <div className="space-y-4">
              {filteredDuas.map((dua) => {
                const IconComponent = dua.icon;
                return (
                  <div 
                    key={dua.id}
                    className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <span className="inline-block px-2 py-1 bg-stone-100 rounded text-xs font-medium text-[#57534E] mb-2">
                          {lang === 'hr' ? dua.category_hr : dua.category_en}
                        </span>
                        <h3 className="text-lg font-heading font-semibold text-[#292524]">
                          {lang === 'hr' ? dua.title_hr : dua.title_en}
                        </h3>
                      </div>
                    </div>

                    {/* Arabic Text */}
                    <div className="bg-primary/5 rounded-xl p-6 mb-4">
                      <p className="font-arabic text-2xl md:text-3xl text-[#292524] text-right leading-[2]" dir="rtl">
                        {dua.arabic}
                      </p>
                    </div>

                    {/* Transliteration */}
                    <p className="text-sm text-primary italic mb-3">
                      {dua.transliteration}
                    </p>

                    {/* Translation */}
                    <p className="text-[#57534E] leading-relaxed">
                      {lang === 'hr' ? dua.translation_hr : dua.translation_en}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
